import { useTranslation } from "react-i18next";
import type { Locale } from "@nh/shared";
import type { CartEntry } from "../cart.pricing";
import { CartLineItem } from "./CartLineItem";
import { CartLineUnavailable } from "./CartLineUnavailable";

export interface CartLineListProps {
  entries: readonly CartEntry[];
  locale: Locale;
  onQuantityChange: (productId: string, quantity: number, stock?: number) => void;
  onRemove: (productId: string) => void;
  /** Se llama cuando una fila navega a la ficha: el panel se cierra. */
  onNavigate: () => void;
}

/**
 * Las filas del carrito.
 *
 * Es una `<ul>` con nombre: sin él, "lista" a secas no le dice nada a
 * quien navega saltando entre regiones, y en el panel hay más de una cosa
 * que podría serlo.
 *
 * Quién pinta qué fila se decide AQUÍ y no dentro de la fila: una línea
 * con producto y una sin él no comparten ni la forma ni los controles, y
 * un solo componente con un `if` gigante dentro acaba mezclando las dos
 * mitades.
 */
export function CartLineList({
  entries,
  locale,
  onQuantityChange,
  onRemove,
  onNavigate,
}: CartLineListProps) {
  const { t } = useTranslation();

  return (
    <ul
      aria-label={t("Artículos del carrito")}
      className="divide-y divide-ds-border px-5"
    >
      {entries.map(({ line, product, price }) =>
        product ? (
          <CartLineItem
            key={line.productId}
            product={product}
            quantity={line.quantity}
            price={price}
            locale={locale}
            onQuantityChange={(quantity) =>
              onQuantityChange(line.productId, quantity, product.stock)
            }
            onRemove={() => onRemove(line.productId)}
            onNavigate={onNavigate}
          />
        ) : (
          <CartLineUnavailable
            key={line.productId}
            line={line}
            onRemove={() => onRemove(line.productId)}
          />
        ),
      )}
    </ul>
  );
}
